/**
 * 자동 재접속 WebSocket 래퍼.
 *
 * 지수 백오프(1→8초) 재접속 + **50분 선제 순환**.
 *
 * Cloud Run의 요청 타임아웃(최대 60분)에 걸려 강제로 끊기느니, 끊기는 시점을 우리가
 * 고른다. 연결 후 `cycleAfterMs`가 지나면 `onCycleDue`를 부르고, 화면 코드가 자막 확정
 * 직후에 `cycle`을 보내 스스로 재접속·`resume`한다.
 */
import { decodeClientMessage } from './protocol';
import type { UnknownMessage } from './protocol';

export type WsStatus = 'connecting' | 'open' | 'closed';

const BACKOFF_STEPS_MS = [1000, 2000, 4000, 8000] as const;

/** 선제 순환 기본 시점 */
export const DEFAULT_CYCLE_AFTER_MS = 50 * 60 * 1000;

export interface WsClientHandlers {
  /** 연결 상태가 바뀔 때마다 호출. detail은 사람이 읽을 보조 설명. */
  onStatus?: (status: WsStatus, detail: string) => void;
  /** 서버가 보낸 JSON 텍스트 프레임 */
  onMessage?: (msg: UnknownMessage) => void;
  /** 서버가 보낸 바이너리 프레임(오디오). */
  onBinary?: (data: ArrayBuffer) => void;
  /** 연결이 열린 직후. 여기서 auth/hello 같은 첫 메시지를 보낸다. */
  onOpen?: () => void;
  /** 닫힘 통지. false를 돌려주면 재접속하지 않는다(예: 인증 실패 4401). */
  shouldReconnect?: (code: number, reason: string) => boolean;
  /** 선제 순환 시점이 됐다. 화면 코드가 적절한 시점에 `cycle`을 보낸다. */
  onCycleDue?: () => void;
  /** 선제 순환 시점. 생략하면 50분. 0이면 끄기(대조 실행용). */
  cycleAfterMs?: number;
}

export class WsClient {
  private ws: WebSocket | null = null;
  private retry = 0;
  private timer: number | null = null;
  private cycleTimer: number | null = null;
  private openedAt = 0;
  private stopped = false;

  constructor(
    private readonly path: string,
    private readonly handlers: WsClientHandlers,
  ) {}

  /** 현재 페이지 기준 WebSocket URL. dev에서는 Vite 프록시가 서버로 넘긴다. */
  private url(): string {
    const scheme = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${scheme}//${location.host}${this.path}`;
  }

  connect(): void {
    this.stopped = false;
    this.clearTimer();
    if (this.ws !== null) return;

    this.handlers.onStatus?.('connecting', '연결 중…');
    const ws = new WebSocket(this.url());
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    ws.addEventListener('open', () => {
      this.retry = 0;
      this.openedAt = Date.now();
      this.startCycleTimer();
      this.handlers.onStatus?.('open', '연결됨');
      this.handlers.onOpen?.();
    });

    ws.addEventListener('message', (ev: MessageEvent<unknown>) => {
      if (typeof ev.data === 'string') {
        const msg = decodeClientMessage(ev.data);
        if (msg !== null) this.handlers.onMessage?.(msg);
        return;
      }
      if (ev.data instanceof ArrayBuffer) this.handlers.onBinary?.(ev.data);
    });

    ws.addEventListener('error', () => {
      // 상세 사유는 close 이벤트에서 전달된다.
      this.handlers.onStatus?.('connecting', '연결 오류 — 재시도합니다.');
    });

    ws.addEventListener('close', (ev: CloseEvent) => {
      this.ws = null;
      this.clearCycleTimer();
      const detail = `연결 종료 (code=${ev.code}${ev.reason !== '' ? `, ${ev.reason}` : ''})`;
      const allowed = this.handlers.shouldReconnect?.(ev.code, ev.reason) ?? true;
      if (this.stopped || !allowed) {
        this.handlers.onStatus?.('closed', detail);
        return;
      }
      const delay = BACKOFF_STEPS_MS[Math.min(this.retry, BACKOFF_STEPS_MS.length - 1)] ?? 8000;
      this.retry += 1;
      this.handlers.onStatus?.('closed', `${detail} — ${delay / 1000}초 후 재접속`);
      this.timer = window.setTimeout(() => this.connect(), delay);
    });
  }

  /** JSON 텍스트 프레임 전송. 연결 전이면 false. */
  send(msg: unknown): boolean {
    if (this.ws === null || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(JSON.stringify(msg));
    return true;
  }

  /** 바이너리 프레임 전송(입력 오디오). */
  sendBinary(data: BufferSource): boolean {
    if (this.ws === null || this.ws.readyState !== WebSocket.OPEN) return false;
    this.ws.send(data);
    return true;
  }

  get isOpen(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /** 현재 연결이 열린 지 얼마나 됐나(ms). 연결 전이면 0. */
  get connectionAgeMs(): number {
    return this.openedAt === 0 ? 0 : Date.now() - this.openedAt;
  }

  private startCycleTimer(): void {
    this.clearCycleTimer();
    const after = this.handlers.cycleAfterMs ?? DEFAULT_CYCLE_AFTER_MS;
    if (after <= 0 || this.handlers.onCycleDue === undefined) return;
    this.cycleTimer = window.setTimeout(() => {
      this.cycleTimer = null;
      this.handlers.onCycleDue?.();
    }, after);
  }

  private clearCycleTimer(): void {
    if (this.cycleTimer !== null) {
      window.clearTimeout(this.cycleTimer);
      this.cycleTimer = null;
    }
  }

  /** 재접속을 멈추고 연결을 닫는다. */
  close(code = 1000, reason = 'client closed'): void {
    this.stopped = true;
    this.clearTimer();
    this.clearCycleTimer();
    this.retry = 0;
    this.ws?.close(code, reason);
    this.ws = null;
  }

  private clearTimer(): void {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
